import type { FileRecord } from "@/common/define";
import avatar from "@/assets/avatar.svg";
import type { PermissionDto } from "../services/files.service";

export interface FilePermissions {
  READ: boolean;
  EDIT: boolean;
  DELETE: boolean;
  UPLOAD: boolean;
  DOWNLOAD: boolean;
  SHARE: boolean;
  COMMENT: boolean;
  CREATE: boolean;
  READ_F: boolean;
  DELETE_F: boolean;
  EDIT_F: boolean;
  SHARE_F: boolean;
}

export const formatFileSize = (sizeInBytes?: number): string => {
  const sizeNumber = Number(sizeInBytes);

  if (sizeInBytes == null || Number.isNaN(Number(sizeInBytes))) {
    return "";
  }

  if (sizeNumber < 0) {
    return "0";
  }


  if (sizeInBytes === 0) {
    return String(sizeInBytes);
  }

  const units = ["B", "KB", "MB", "GB", "TB"];
  let size = sizeNumber;
  let unitIndex = 0;

  while (size >= 1024 && unitIndex < units.length - 1) {
    size /= 1024;
    unitIndex++;
  }

  const formatted =
    Number.isInteger(size) ? size.toString() : size.toFixed(2).replace(/\.?0+$/, "");

  return `${formatted} ${units[unitIndex]}`;

};

// Map PermissionDto[] from API to FilePermissions object
export const mapPermissions = (
  permissions: PermissionDto[]
): FilePermissions => {
  const permissionMap: FilePermissions = {
    READ: false,
    EDIT: false,
    DELETE: false,
    UPLOAD: false,
    DOWNLOAD: false,
    SHARE: false,
    COMMENT: false,
    CREATE: false,
    READ_F: false,
    DELETE_F: false,
    EDIT_F: false,
    SHARE_F: false,
  };

  permissions.forEach((permission) => {
    const code = permission.code?.toUpperCase();
    switch (code) {
      case "READ":
      case "VIEW":
        permissionMap.READ = true;
        break;
      case "EDIT":
      case "MODIFY":
        permissionMap.EDIT = true;
        break;
      case "DELETE":
        permissionMap.DELETE = true;
        break;
      case "UPLOAD":
        permissionMap.UPLOAD = true;
        break;
      case "DOWNLOAD":
        permissionMap.DOWNLOAD = true;
        break;
      case "SHARE":
        permissionMap.SHARE = true;
        break;
      case "COMMENT":
        permissionMap.COMMENT = true;
        break;
      case "CREATE":
        permissionMap.CREATE = true;
        break;
      case "READ_F":
      case "READ_FOLDER":
        permissionMap.READ_F = true;
        break;
      case "DELETE_F":
      case "DELETE_FOLDER":
        permissionMap.DELETE_F = true;
        break;
      case "EDIT_F":
      case "EDIT_FOLDER":
        permissionMap.EDIT_F = true;
        break;
      case "SHARE_F":
      case "SHARE_FOLDER":
        permissionMap.SHARE_F = true;
        break;
    }
  });

  return permissionMap;
};

// Get full permissions for Files page
export const getFullPermissions = (): FilePermissions => ({
  READ: true,
  EDIT: true,
  DELETE: true,
  UPLOAD: true,
  DOWNLOAD: true,
  SHARE: true,
  COMMENT: true,
  CREATE: true,
  READ_F: true,
  DELETE_F: true,
  EDIT_F: true,
  SHARE_F: true,
});

// Apply permissions to a file record based on context
export interface ApplyPermissionsOptions {
  inheritedPermissions?: FilePermissions;
  isFilesPage?: boolean;
  apiPermissions?: PermissionDto[];
}

export const applyPermissions = (
  record: FileRecord,
  options: ApplyPermissionsOptions = {}
): FileRecord => {
  const { inheritedPermissions, isFilesPage = false, apiPermissions } = options;

  let permissions: FilePermissions;

  if (isFilesPage) {
    // Files page - full permissions always
    permissions = getFullPermissions();
  } else if (inheritedPermissions) {
    // Inside a shared folder - inherit parent permissions
    permissions = inheritedPermissions;
  } else if (apiPermissions && apiPermissions.length > 0) {
    // Shared items root level - use API permissions
    permissions = mapPermissions(apiPermissions);
  } else {
    // Fallback - no permissions
    permissions = {
      READ: false,
      EDIT: false,
      DELETE: false,
      UPLOAD: false,
      DOWNLOAD: false,
      SHARE: false,
      COMMENT: false,
      CREATE: false,
      READ_F: false,
      DELETE_F: false,
      EDIT_F: false,
      SHARE_F: false,
    };
  }

  return {
    ...record,
    permissions,
  };
};

export const formatDate = (dateString: string): string => {
  return new Date(dateString).toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
};

export function formatDuration(seconds: number): string {
  if (!isFinite(seconds)) return "0s";

  const totalSeconds = Math.floor(seconds);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const secs = seconds % 60;

  const parts: string[] = [];

  if (hours > 0) {
    parts.push(`${hours}h`);
  }

  if (minutes > 0 || hours > 0) {
    parts.push(`${minutes}m`);
  }

  // Giữ 2 chữ số thập phân nếu < 1 phút
  if (hours === 0 && minutes === 0) {
    parts.push(`${secs.toFixed(2)}s`);
  } else {
    parts.push(`${Math.floor(secs)}s`);
  }

  return parts.join(" ");
}

export const mapApiToFolderRecord = (apiData: any): FileRecord => {

  if (!apiData.id) {
    console.warn('[mapApiToFolderRecord] Missing id for record:', apiData);
  }

const format =
  apiData.type === 'folder'
    ? apiData.type.toUpperCase()
    : apiData.name?.match(/\.([^.]+)$/)?.[1]?.toUpperCase() ||
      apiData.type?.toUpperCase() ||
      "";
  return {
    ...apiData,
    id: apiData.id ?? `temp-${Date.now()}-${Math.random()}`,
    key: apiData.id ?? `temp-${Date.now()}-${Math.random()}`,
    name: apiData.displayName ?? apiData.name ?? 'Unnamed',
    version: apiData.version || "",
    format: format,
    owner: apiData.owner?.name || apiData.createUser?.name || "Unknown",
    versionUploadedBy:
      apiData.owner?.name || apiData.createUser?.name || "Unknown",
    avatar: apiData.owner?.avatar || avatar,
    lastUploaded: formatDate(apiData.lastModified),
    size: formatFileSize(apiData.size ?? 0),
    sizeBytes: apiData.size ?? 0,
    type: apiData.type || "",
    author: apiData.author || "",
    originator: apiData.originator || "",
    location: apiData.location || "",
    cloudLink: apiData.cloudLink || "",
    bucketName: apiData.bucketName || "",
    children: [],
    isRootOrganization: apiData.isRootOrganization || false,
  };
};

export const getUniqueRecords = (records: FileRecord[]): FileRecord[] => {
  const uniqueKeys = new Set();
  return records
    .filter((record) => {
      const key = record.key || record.id;
      if (key && uniqueKeys.has(key)) {
        return false;
      }
      if (key) uniqueKeys.add(key);
      return true;
    })
    .map((record, index) => ({
      ...record,
      key: record.key || record.id || `record-${index}`,
    }));
};
